import { sectionRegistry } from '../canvas/sectionRegistry';
import { bt } from '../i18n';
import type { SectionInstance } from '@aquilixshop/ui-react';

/**
 * The DISPLAY NAME of a section instance — the registry label, unless a settings knob has changed the
 * section's anatomy enough that the generic label lies about what's on screen.
 *
 * Why this exists (founder 2026-08-01): `testimonials` with `cardStyle: 'media-overlay'` renders large
 * media cards with a name + role overlay and a play badge — no quote, no stars, no review text. The rail,
 * the inspector header and the canvas all called it "Testimonials", the same name as the quote grid two
 * rows below it, so the tree read as two identical entries for two unrelated-looking sections.
 *
 * Kept as a small explicit table rather than a `meta.variantLabel` hook in `@aquilixshop/ui-react`: a rail
 * label is builder-only chrome, and the registry ships to the storefront too. Add a row here when a knob
 * genuinely changes what a section IS — not for every cosmetic option.
 */
const VARIANT_LABELS: Readonly<
  Record<string, (settings: Record<string, unknown>) => string | null>
> = {
  testimonials: (s) =>
    s.cardStyle === 'media-overlay' ? bt('secname.testimonials.video', 'Video testimonials') : null,
};

/**
 * Display name for a section TYPE — the registry label, translated. The registry itself ships to the
 * storefront and must never carry seller vocabulary, so the swap happens here, at the builder's seam,
 * and every surface that names a type (rail, inspector header, add-section picker) goes through it.
 */
export function sectionTypeName(type: string, floor: string): string {
  return bt(`secname.${type}`, floor);
}

/** Display name for a section instance. Falls back to the registry label, then the raw type. */
export function sectionDisplayName(section: SectionInstance | undefined): string {
  if (!section) return bt('secname.fallback', 'Section');
  const variant = VARIANT_LABELS[section.type]?.(
    (section.settings ?? {}) as Record<string, unknown>,
  );
  if (variant) return variant;
  // The registry label is builder-only DISPLAY; the registry itself ships to the storefront and
  // must never carry seller vocabulary — so the translation happens here, at the seam.
  const label = sectionRegistry[section.type]?.meta.label;
  return label ? sectionTypeName(section.type, label) : section.type;
}

/**
 * The DISPLAY NAME of a block type — the schema's English `name`, translated. Tried section-scoped
 * first (`blockname.<section>.<type>`) because a block type is only unique WITHIN its section:
 * `column` is a Column in multicolumn, a Testimonial in facebook-testimonials, an Icon in icon-bar.
 * The shared `blockname.<type>` entry serves the 95 types whose name never varies.
 */
export function blockDisplayName(sectionType: string, blockType: string, floor: string): string {
  return bt(`blockname.${sectionType}.${blockType}`, bt(`blockname.${blockType}`, floor));
}
